import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import propTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import { useDispatch, useSelector } from 'react-redux';
import { EntityChip, FormActionButton, InlineCreateSelector } from 'lib';
import {
  createLaborClassification,
  renameLaborClassification,
  deleteLaborClassification,
  selectAllActiveLaborClassifications,
  selectIsProjectFetched,
  selectLaborClassificationById,
  selectProjectRates,
} from 'store/laborClassifications';

const NUMERIC_INPUT_REGEX = /^\d*[.,]?\d*$/;
const EMPTY_DRAFT = { earningsCode: '', baseRate: '', fringeBenefit: '' };

const parseNumeric = (raw) => {
  if (raw === null || raw === undefined) return undefined;
  const trimmed = String(raw).trim();
  if (trimmed === '') return undefined;
  const normalized = trimmed.replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const formatNumeric = (value) => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const buildDraftFromRate = (rate) => ({
  earningsCode: rate?.earningsCode ?? '',
  baseRate: formatNumeric(rate?.baseRate),
  fringeBenefit: formatNumeric(rate?.fringeBenefit),
});

const LaborClassificationsSection = ({
  classes,
  values,
  setFieldValue,
  project,
  openSnackbar,
}) => {
  const dispatch = useDispatch();
  const allActive = useSelector(selectAllActiveLaborClassifications);
  const sliceRates = useSelector((state) => selectProjectRates(state, project?._id));
  const sliceFetched = useSelector((state) => selectIsProjectFetched(state, project?._id));

  const [editingLcId, setEditingLcId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editorError, setEditorError] = useState(null);

  const defaultId = values.defaultLaborClassificationId || null;
  const alternateIds = useMemo(
    () => values.alternateLaborClassifications || [],
    [values.alternateLaborClassifications],
  );
  const formRates = useMemo(
    () => values.laborClassificationRates || {},
    [values.laborClassificationRates],
  );

  // Hydrate Formik rates once the slice's project-scoped fetch completes.
  // Guarded by a ref so subsequent slice updates (e.g. from a save) don't
  // clobber unsaved user edits in the form.
  const hydratedForRef = useRef(null);
  useEffect(() => {
    if (!project?._id) return;
    if (!sliceFetched) return;
    if (hydratedForRef.current === project._id) return;
    setFieldValue('laborClassificationRates', { ...sliceRates });
    hydratedForRef.current = project._id;
  }, [project?._id, sliceFetched, sliceRates, setFieldValue]);

  const defaultLc = useSelector((state) => selectLaborClassificationById(state, defaultId));
  const lcById = useMemo(() => {
    const map = {};
    allActive.forEach((lc) => {
      map[lc._id] = lc;
    });
    if (defaultLc) map[defaultLc._id] = defaultLc;
    return map;
  }, [allActive, defaultLc]);

  const handleCreate = useCallback(
    async (name) => {
      try {
        const created = await dispatch(createLaborClassification(name));
        openSnackbar?.('success', 'Successfully Added!');
        return created;
      } catch (err) {
        if (err?.status === 409) {
          openSnackbar?.(
            'error',
            `Labor Classification "${err.value || name}" already exists`,
          );
        } else {
          openSnackbar?.('error', err?.message || 'Failed to create Labor Classification');
        }
        throw err;
      }
    },
    [dispatch, openSnackbar],
  );

  const handleRename = useCallback(
    async (id, name) => {
      try {
        await dispatch(renameLaborClassification({ id, name }));
      } catch (err) {
        if (err?.status === 409) {
          openSnackbar?.(
            'error',
            `Labor Classification "${err.value || name}" already exists`,
          );
        } else {
          openSnackbar?.('error', err?.message || 'Failed to rename Labor Classification');
        }
        throw err;
      }
    },
    [dispatch, openSnackbar],
  );

  const handleDelete = useCallback(
    async (id) => {
      try {
        await dispatch(deleteLaborClassification(id));
        if (defaultId === id) setFieldValue('defaultLaborClassificationId', null);
        if (alternateIds.includes(id)) {
          setFieldValue(
            'alternateLaborClassifications',
            alternateIds.filter((x) => x !== id),
          );
        }
        openSnackbar?.('success', 'Labor Classification Successfully Deleted!');
      } catch (err) {
        openSnackbar?.('error', err?.message || 'Failed to delete Labor Classification');
        throw err;
      }
    },
    [dispatch, defaultId, alternateIds, setFieldValue, openSnackbar],
  );

  const handleDefaultChange = useCallback(
    (id) => {
      setFieldValue('defaultLaborClassificationId', id || null);
      if (id && alternateIds.includes(id)) {
        setFieldValue(
          'alternateLaborClassifications',
          alternateIds.filter((x) => x !== id),
        );
      }
      // Open for editing
      setDraft(buildDraftFromRate(formRates[id]));
      setEditorError(null);
      setEditingLcId(id);
    },
    [setFieldValue, alternateIds, formRates],
  );

  const handleAlternatesChange = useCallback(
    (ids) => {
      const next = Array.isArray(ids) ? ids.filter((x) => x !== defaultId) : [];
      setFieldValue('alternateLaborClassifications', next);
      const addedId = ids.filter(id => !alternateIds.includes(id))[0];
      const removedId = alternateIds.filter(id => ids.includes(id))[0];

      if (removedId) {
        setEditingLcId(null);
        setDraft(EMPTY_DRAFT);
      } if (addedId) {
        setDraft(buildDraftFromRate(formRates[addedId]));
        setEditingLcId(addedId);
      }
      setEditorError(null);
    },
    [setFieldValue, defaultId, alternateIds, formRates],
  );

  // Note: when the removed lc is the one currently being edited, the
  // `editingLcId / chipIds` effect below will close the editor on the next
  // render — no need to duplicate that cleanup here.
  const handleRemoveChip = useCallback(
    (lcId) => {
      if (defaultId === lcId) {
        setFieldValue('defaultLaborClassificationId', null);
        return;
      }
      if (alternateIds.includes(lcId)) {
        setFieldValue(
          'alternateLaborClassifications',
          alternateIds.filter((x) => x !== lcId),
        );
      }
    },
    [defaultId, alternateIds, setFieldValue],
  );

  const openEditor = useCallback(
    (lcId) => {
      setDraft(buildDraftFromRate(formRates[lcId]));
      setEditorError(null);
      setEditingLcId(lcId);
    },
    [formRates],
  );

  const closeEditor = useCallback(() => {
    setEditingLcId(null);
    setDraft(EMPTY_DRAFT);
    setEditorError(null);
  }, []);

  const handleDraftChange = useCallback(
    (key) => (e) => {
      const value = e?.target?.value ?? '';
      setDraft((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleNumericChange = useCallback(
    (key) => (e) => {
      const value = e?.target?.value ?? '';
      if (!NUMERIC_INPUT_REGEX.test(value)) return;
      setDraft((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleEditorApply = useCallback(() => {
    if (!editingLcId) return;
    const baseRate = parseNumeric(draft.baseRate);
    const fringeBenefit = parseNumeric(draft.fringeBenefit);
    if (Number.isNaN(baseRate) || Number.isNaN(fringeBenefit)) {
      setEditorError('Base rate and Fringe benefit must be numeric.');
      return;
    }
    const earningsCodeRaw = draft.earningsCode.trim();
    const next = {
      earningsCode: earningsCodeRaw === '' ? null : earningsCodeRaw,
      baseRate: baseRate === undefined ? null : baseRate,
      fringeBenefit: fringeBenefit === undefined ? null : fringeBenefit,
    };
    setFieldValue('laborClassificationRates', {
      ...formRates,
      [editingLcId]: next,
    });
    closeEditor();
  }, [editingLcId, draft, formRates, setFieldValue, closeEditor]);

  const chipIds = useMemo(() => {
    const ids = [];
    if (defaultId) ids.push(defaultId);
    alternateIds.forEach((id) => {
      if (id && id !== defaultId && !ids.includes(id)) ids.push(id);
    });
    return ids;
  }, [defaultId, alternateIds]);

  // Close the editor automatically when its target LC is no longer chipped —
  // e.g. user removed it via the Default/Alternates pickers while the editor
  // was open. Without this guard, Save would 400 on BE (LC not in default ∪ alternates).
  useEffect(() => {
    if (editingLcId && !chipIds.includes(editingLcId)) closeEditor();
  }, [editingLcId, chipIds, closeEditor]);

  const editingLcName = editingLcId ? (lcById[editingLcId]?.name || '') : '';

  return (
    <>
      <Grid item xs={12} lg={6} className={classes.zeroPaddingTop}>
        <label htmlFor="defaultLaborClassificationId">
          <Typography variant="body1" color="textSecondary">
            Default Labor Classification:
          </Typography>
          <InlineCreateSelector
            id="defaultLaborClassificationId"
            name="defaultLaborClassificationId"
            value={defaultId}
            options={allActive}
            onChange={handleDefaultChange}
            onCreate={handleCreate}
            onRename={handleRename}
            onDelete={handleDelete}
            excludeIds={alternateIds}
            addLabel="+ Add Labor Classification"
            placeholder="Select"
            placeholderOnly
          />
        </label>
      </Grid>
      <Grid item xs={12} lg={6} className={classes.zeroPaddingTop}>
        <label htmlFor="alternateLaborClassifications">
          <Typography variant="body1" color="textSecondary">
            All Labor Classifications:
          </Typography>
          <InlineCreateSelector
            id="alternateLaborClassifications"
            name="alternateLaborClassifications"
            multi
            value={alternateIds}
            options={allActive}
            onChange={handleAlternatesChange}
            onCreate={handleCreate}
            onRename={handleRename}
            onDelete={handleDelete}
            excludeIds={defaultId ? [defaultId] : []}
            addLabel="+ Add Labor Classification"
            placeholder="Select"
            placeholderOnly
          />
        </label>
      </Grid>
      {chipIds.length > 0 && (
        <Grid item xs={12} className={classes.zeroPaddingTop}>
          {!editingLcId && (
            <Typography variant="body1" color="textSecondary">
              Click to edit Labor Classifications:
            </Typography>
          )}
          <div className={classes.chipStrip}>
            {chipIds.map((lcId) => {
              const lc = lcById[lcId];
              if (!lc) return null;
              return (
                <EntityChip
                  key={lcId}
                  label={lc.name}
                  badge={lcId === defaultId ? '(default)' : null}
                  onEdit={() => openEditor(lcId)}
                  onRemove={() => handleRemoveChip(lcId)}
                  editable
                  editTitle="Click to edit rates"
                  selected={editingLcId === lcId}
                />
              );
            })}
          </div>
        </Grid>
      )}
      {editingLcId && (
        <Grid item xs={12} lg={12} className={classes.zeroPaddingTop}>
          <Typography variant="h6" color="textPrimary" style={{ margin: '5px 0' }}>
            {editingLcName ? `${editingLcName}:` : 'Edit Labor Classification'}
          </Typography>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} sm={6} lg={3}>
              <label htmlFor="editLcEarningsCode">
                <Typography variant="body1" color="textSecondary">
                  Earning code:
                </Typography>
                <TextField
                  id="editLcEarningsCode"
                  name="editLcEarningsCode"
                  value={draft.earningsCode}
                  onChange={handleDraftChange('earningsCode')}
                  className={classes.textField}
                  variant="outlined"
                  inputProps={{ 'aria-label': 'Earning code' }}
                  fullWidth
                />
              </label>
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <label htmlFor="editLcBaseRate">
                <Typography variant="body1" color="textSecondary">
                  Base rate:
                </Typography>
                <TextField
                  id="editLcBaseRate"
                  name="editLcBaseRate"
                  value={draft.baseRate}
                  onChange={handleNumericChange('baseRate')}
                  className={classes.textField}
                  variant="outlined"
                  inputProps={{ 'aria-label': 'Base rate', inputMode: 'decimal' }}
                  fullWidth
                />
              </label>
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <label htmlFor="editLcFringeBenefit">
                <Typography variant="body1" color="textSecondary">
                  Fringe benefit:
                </Typography>
                <TextField
                  id="editLcFringeBenefit"
                  name="editLcFringeBenefit"
                  value={draft.fringeBenefit}
                  onChange={handleNumericChange('fringeBenefit')}
                  className={classes.textField}
                  variant="outlined"
                  inputProps={{ 'aria-label': 'Fringe benefit', inputMode: 'decimal' }}
                  fullWidth
                />
              </label>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              lg={3}
              container
              spacing={2}
              justifyContent="flex-end"
              alignItems="center"
            >
              <div className={classes.rateEditorActions}>
                <FormActionButton size="small" color="secondary" onClick={closeEditor}>
                  Cancel
                </FormActionButton>
                <FormActionButton size="small" color="primary" onClick={handleEditorApply}>
                  Save
                </FormActionButton>
              </div>
            </Grid>
          </Grid>
          {editorError && <div className={classes.error}>{editorError}</div>}
        </Grid>
      )}
    </>
  );
};

LaborClassificationsSection.propTypes = {
  classes: propTypes.object.isRequired,
  values: propTypes.object.isRequired,
  setFieldValue: propTypes.func.isRequired,
  project: propTypes.object,
  openSnackbar: propTypes.func,
};

LaborClassificationsSection.defaultProps = {
  project: null,
  openSnackbar: undefined,
};

export default LaborClassificationsSection;
